import { Dialog, IconButton, SxProps, Tooltip, Typography, TypographyProps } from '@mui/material';
import { X } from 'lucide-react';
import React, { ReactNode, useState } from 'react';
import { POPUP_MAX_EXTENT, POPUP_SIZES, type PopupSize, type PopupSizeRule } from '@/styles/popup';
import {
  HeaderActions,
  StepButton,
  StepContainer,
  StepIndicator,
  StepLabel,
  StepWrapper,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
  TitleContent,
} from './styles';

export interface Step {
  tooltip?: string;
  label?: string;
  active: boolean;
  completed?: boolean;
  /**
   * The run has already been here, so the stepper may jump back to it.
   *
   * Distinct from `completed`: a step can be reachable without having been
   * completed (the auditor stepped back past it), and a flow with no back
   * navigation leaves this unset entirely — which is the default, so a stepper
   * stays pure orientation until a caller opts into navigation.
   */
  reachable?: boolean;
}

interface ModalProps {
  open: boolean;
  children: ReactNode;
  /**
   * The pop-up size: small 600 · medium 700 · large 1200 (`@/styles/popup`, Figma
   * `40156:67999`). This is the current global rule and the one every dialog uses —
   * new and reworked dialogs pass `size`, never a width of their own.
   */
  size?: PopupSize;
  /**
   * @deprecated A free width from before the global sizes. Kept only until each existing
   * dialog moves onto `size`, which overrides it when both are passed. One caller has no
   * size to move onto: the import wizard's judgement steps pass `false` to fill the
   * screen less the inset (`docs/DESIGN_SYSTEM.md` §Dialogs, #614).
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number | false;
  /** @deprecated Goes with `maxWidth`; `size` overrides it when both are passed. */
  fullWidth?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  onClose: () => void;
  className?: string;
  'data-testid'?: string;
}

interface ModalHeaderProps {
  title?: string | ReactNode;
  titleVariant?: 'h5' | 'h6' | 'subtitle1';
  titleProps?: TypographyProps;
  closeButton?: boolean;
  steps?: Step[];
  showSteps?: boolean;
  /**
   * Jump back to a reached step. Absent leaves every dot inert, which is what a
   * stepper that only reports progress should be.
   *
   * Only ever fired for a step marked `reachable` and not already `active` —
   * forward movement belongs to the flow's own gates, never to this control.
   */
  onStepClick?: (index: number) => void;
  /** Accessible name for the stepper group, e.g. "Import progress". */
  stepsLabel?: string;
  /**
   * Accessible name for the ✕. A prop rather than a `useTranslation` call because
   * `Modal` is the lowest-level primitive here — every dialog in the app mounts it,
   * including in tests that wrap no i18n provider — so the copy comes from the
   * caller, who has the namespace anyway. The default is a last resort: an English
   * name is worse than a translated one and far better than the unlabelled icon
   * button this used to render.
   */
  closeLabel?: string;
  /**
   * Also show `closeLabel` as a hover tooltip on the ✕. Opt-in, because on most
   * dialogs "Close" over an ✕ is noise; a caller whose close means something less
   * obvious (the import wizard's ✕ collapses a run it keeps, §6.2.18) names that
   * gesture here. One string serves both the tooltip and the accessible name, so
   * what a sighted user reads on hover is what a screen reader announces.
   */
  showCloseTooltip?: boolean;
  onClose?: () => void;
}

interface ModalBodyProps {
  children: ReactNode;
  sx?: SxProps;
  /**
   * Forwarded to the DOM. TypeScript admits any `data-*` prop on a component, so a
   * caller's `data-testid` compiled fine and was silently dropped here — the import
   * wizard's `import-wizard-body` never reached the page, and the E2E spec that
   * waited for it could not pass.
   */
  'data-testid'?: string;
}

interface ModalFooterProps {
  children: ReactNode;
  sx?: SxProps;
  'data-testid'?: string;
}

type ModalStepsProps = Required<Pick<ModalHeaderProps, 'steps'>> &
  Pick<ModalHeaderProps, 'onStepClick' | 'stepsLabel'>;

/**
 * The stepper dots, and the ONE tooltip they share (#620).
 *
 * Each dot used to own an uncontrolled tooltip. MUI opens the next one at once while
 * the last is still fading out, and a hoverable popper sat right above the neighbouring
 * dots, 12px away, so a sweep across the stepper stacked several step names above it.
 * Now one piece of state names the dot whose tooltip is showing, so two cannot be open,
 * and a closing tooltip goes without a fade.
 */
function ModalSteps({ steps, onStepClick, stepsLabel }: ModalStepsProps) {
  // The dot whose tooltip is open, and whether it was a button when the tooltip opened.
  const [openDot, setOpenDot] = useState<{ index: number; navigable: boolean } | null>(null);

  // Reachable, not active, and somebody is listening: anything less and
  // the dot stays a dot. `active` is excluded because navigating to
  // where you already are is a no-op dressed as an affordance.
  const isNavigable = (step: Step) =>
    Boolean(onStepClick) && Boolean(step.reachable) && !step.active;

  // A dot that changed kind since its tooltip opened loses it for good. A click (it
  // becomes the active step) or a pulled-back reach swaps the button for a plain dot
  // under the pointer: a new element the pointer never entered, which never reports a
  // leave. MUI reports a close only for a tooltip that is open, so a tooltip merely
  // hidden would pop back up, unhovered, once the dot turned back. Adjusted during
  // render, so no effect.
  if (
    openDot &&
    (openDot.index >= steps.length || isNavigable(steps[openDot.index]) !== openDot.navigable)
  ) {
    setOpenDot(null);
  }

  return (
    <StepContainer role='group' aria-label={stepsLabel} onMouseLeave={() => setOpenDot(null)}>
      {steps.map((step, index) => {
        const navigable = isNavigable(step);
        const body = (
          <>
            {step.label && <StepLabel active={step.active}>{step.label}</StepLabel>}
            <StepIndicator
              active={step.active}
              completed={step.completed}
              aria-current={step.active ? 'step' : undefined}
              data-testid={`modal-step-${index}-dot`}
            />
          </>
        );

        return (
          <Tooltip
            arrow
            placement='top'
            key={index}
            title={step.tooltip || ''}
            open={openDot?.index === index}
            onOpen={() => setOpenDot({ index, navigable })}
            // Clears only its own: the next dot may already have taken over by the time
            // this one's close lands.
            onClose={() => setOpenDot((current) => (current?.index === index ? null : current))}
            // A dot that is not a button is a plain <div>: MUI's default would name it with
            // `aria-label`, which axe refuses on an element with no role
            // (`aria-prohibited-attr`). Described instead: `title` while closed,
            // `aria-describedby` while open. A button keeps its own `aria-label` below.
            describeChild={!navigable}
            // Nothing in a step name to hover, and a hoverable popper kept the tooltip
            // open while the pointer crossed the dots beneath it.
            disableInteractive
            // No fade-out. MUI closes a dot's tooltip a tick before it opens the next
            // one's, so a fading tooltip would still be showing beside its successor.
            slotProps={{ transition: { exit: false } }}
          >
            {navigable ? (
              <StepButton
                type='button'
                // The dots carry no visible text, so the accessible name has to
                // come from the caller's own copy — the same string the tooltip
                // shows, rather than a "Step 3" the screen reader alone would hear.
                aria-label={step.tooltip || step.label}
                onClick={() => onStepClick?.(index)}
                data-testid={`modal-step-${index}`}
              >
                {body}
              </StepButton>
            ) : (
              <StepWrapper data-testid={`modal-step-${index}`}>{body}</StepWrapper>
            )}
          </Tooltip>
        );
      })}
    </StepContainer>
  );
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  titleVariant = 'h6',
  titleProps = {},
  closeButton = true,
  steps,
  showSteps = false,
  onStepClick,
  stepsLabel,
  closeLabel = 'Close',
  showCloseTooltip = false,
  onClose,
}) => {
  if (!title && !showSteps) return null;

  const hasSteps = showSteps && steps && steps.length > 0;

  return (
    <StyledDialogTitle>
      <TitleContent>
        {typeof title === 'string' ? (
          <Typography variant={titleVariant} {...titleProps}>
            {title}
          </Typography>
        ) : (
          title
        )}
      </TitleContent>
      {/**
       * The stepper and the ✕ render TOGETHER. They used to be alternatives — a
       * header showing steps had no close button at all — which quietly swallowed
       * the `onClose` its only stepped caller was already passing, comment and all
       * ("No X while the job runs"). Every design that carries a stepper also
       * carries a way out, so the either/or was the anomaly.
       *
       * Callers that genuinely want a stepper with no ✕ pass `closeButton={false}`.
       */}
      <HeaderActions>
        {hasSteps && <ModalSteps steps={steps} onStepClick={onStepClick} stepsLabel={stepsLabel} />}
        {closeButton && onClose && (
          <Tooltip arrow placement='top' title={showCloseTooltip ? closeLabel : ''}>
            {/* 24px, the smallest comfortable target; the negative margin keeps the
                glyph on the header's 24px edge, as drawn. */}
            <IconButton
              size='small'
              onClick={onClose}
              aria-label={closeLabel}
              data-testid='modal-close'
              sx={{ mr: -0.5 }}
            >
              <X size={16} />
            </IconButton>
          </Tooltip>
        )}
      </HeaderActions>
    </StyledDialogTitle>
  );
};

const ModalBody: React.FC<ModalBodyProps> = ({ children, sx, 'data-testid': testId }) => {
  return (
    <StyledDialogContent sx={sx} data-testid={testId}>
      {children}
    </StyledDialogContent>
  );
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children, sx, 'data-testid': testId }) => {
  return (
    <StyledDialogActions sx={sx} data-testid={testId}>
      {children}
    </StyledDialogActions>
  );
};

/**
 * A global size as paper geometry. The theme already caps the height and keeps the
 * screen inset (`MuiDialog`), so this only sets the width and the height to grow from;
 * `min()` keeps a starting height from outgrowing a short screen.
 */
const popupPaperSx = ({ width, minHeight }: PopupSizeRule): SxProps => ({
  width,
  maxWidth: POPUP_MAX_EXTENT,
  ...(minHeight === undefined
    ? { height: POPUP_MAX_EXTENT }
    : { minHeight: `min(${minHeight}px, ${POPUP_MAX_EXTENT})` }),
});

const Modal: React.FC<ModalProps> & {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
} = ({
  open,
  onClose,
  children,
  size,
  maxWidth = 'sm',
  fullWidth = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  className,
  'data-testid': dataTestId,
  ...props
}) => {
  // Either a global size or a legacy pixel width sizes the paper directly, so MUI's own
  // breakpoint widths step aside for both.
  const sizeRule = size ? POPUP_SIZES[size] : undefined;
  const paperSx = sizeRule
    ? popupPaperSx(sizeRule)
    : typeof maxWidth === 'number'
      ? { width: maxWidth, maxWidth: maxWidth }
      : undefined;

  return (
    <Dialog
      className={className}
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' && disableBackdropClick) return;
        if (reason === 'escapeKeyDown' && disableEscapeKeyDown) return;
        onClose();
      }}
      maxWidth={sizeRule || typeof maxWidth === 'number' ? false : maxWidth}
      fullWidth={paperSx ? false : fullWidth}
      slotProps={{
        paper: {
          ...(paperSx ? { sx: paperSx } : {}),
          ...(dataTestId ? { 'data-testid': dataTestId } : {}),
        },
      }}
      {...props}
    >
      {children}
    </Dialog>
  );
};

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export type { ModalBodyProps, ModalFooterProps, ModalHeaderProps, ModalProps };
export default Modal;
